import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import SettingsModal from './SettingsModal';
import ConfirmModal from './ConfirmModal';
import AlertModal from './AlertModal';
import DatePicker from './DatePicker';
import TimePicker from './TimePicker';
import './Calendar.css';

const CALL_TYPES = [
  { id: 'general_department', label: 'Общий созвон отделения - начало месяца' },
  { id: 'management_team', label: 'Созвон: Management Team' },
  { id: 'general_department_2', label: 'Общий созвон отделения - конец месяца' }
];

const REMOVED_CALL_TYPES = new Set(['curator_mrp', 'curator_junior_od', 'curator_media']);

// Dynamic participants loaded from server

const EVENT_COLORS = [
  '#22c55e', '#ff8000', '#ff9933', '#f59e0b', '#ef4444', '#ec4899', '#ffb84d', '#ff6a00'
];

const MONTH_NAMES = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
];

const MONTH_NAMES_GEN = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'
];

const WEEK_DAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const WEEK_DAYS_FULL = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'];

function toDateKey(d) {
  if (!d) return '';
  if (typeof d === 'string') return d.slice(0, 10);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getMskToday() {
  const now = new Date();
  return new Date(now.getTime() + (now.getTimezoneOffset() * 60000) + (3 * 3600000));
}

function parseLocalDate(s) {
  if (!s) return new Date();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s));
  if (!m) return new Date();
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function getCallTypeLabel(callType) {
  const t = CALL_TYPES.find(c => c.id === callType);
  return t ? t.label : callType;
}

function formatTime(timeStr) {
  if (!timeStr) return '';
  return String(timeStr).substring(0, 5);
}

function getMonday(d) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

const Calendar = () => {
  const [view, setView] = useState('month');
  const [currentDate, setCurrentDate] = useState(() => {
    const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d;
  });
  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));

  const [calls, setCalls] = useState([]);
  const [customEvents, setCustomEvents] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRoles, setUserRoles] = useState([]);
  const [moderatorsList, setModeratorsList] = useState([]);
  const [participantSearch, setParticipantSearch] = useState('');

  const [selectedDay, setSelectedDay] = useState(null);
  const [dayPanelOpen, setDayPanelOpen] = useState(false);
  const dayPanelRef = useRef(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 1024);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Modal state
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [eventModalTab, setEventModalTab] = useState('call');
  const [editingCall, setEditingCall] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [callForm, setCallForm] = useState({ call_type: '', call_date: '', call_time: '', participants: [] });
  const [eventForm, setEventForm] = useState({ title: '', event_date: '', event_time: '', description: '', color: '#22c55e', participants: [] });

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [alertModal, setAlertModal] = useState({ isOpen: false, title: '', message: '', type: 'error' });

  const showAlert = (title, message, type = 'error') => {
    setAlertModal({ isOpen: true, title, message, type });
  };

  const canEdit = useCallback(() => {
    const allowed = ['Deputy Chief Manager', 'Chief Manager', 'admin'];
    return userRoles.some(r => allowed.includes(r));
  }, [userRoles]);

  // --- Data loading ---
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        const [rolesResp, modsResp] = await Promise.all([
          fetch('/api/auth/check-roles', { headers }),
          fetch('/api/calls-schedule/participants-list', { headers })
        ]);
        if (rolesResp.ok) { const d = await rolesResp.json(); setUserRoles(d.roles || []); }
        if (modsResp.ok) { const mods = await modsResp.json(); setModeratorsList(mods); }
      } catch (e) { console.error('Error loading roles/moderators:', e); }
    })();
  }, []);

  const getDateRange = useCallback(() => {
    if (view === 'month') {
      const start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      // extend to full calendar grid (prev/next month days)
      const startDay = start.getDay();
      const offset = startDay === 0 ? -6 : 1 - startDay;
      const gridStart = new Date(start); gridStart.setDate(gridStart.getDate() + offset);
      const gridEnd = new Date(end);
      const endDay = gridEnd.getDay();
      if (endDay !== 0) gridEnd.setDate(gridEnd.getDate() + (7 - endDay));
      return { startDate: toDateKey(gridStart), endDate: toDateKey(gridEnd), month: currentDate.getMonth() + 1 };
    }
    const end = new Date(weekStart);
    end.setDate(end.getDate() + 6);
    const m1 = weekStart.getMonth() + 1;
    const m2 = end.getMonth() + 1;
    return { startDate: toDateKey(weekStart), endDate: toDateKey(end), month: m1 !== m2 ? null : m1 };
  }, [view, currentDate, weekStart]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { startDate, endDate, month } = getDateRange();
      const headers = { Authorization: `Bearer ${token}` };

      const [callsResp, eventsResp, bdResp] = await Promise.all([
        fetch(`/api/calls-schedule?startDate=${startDate}&endDate=${endDate}`, { headers }),
        fetch(`/api/calendar-events?startDate=${startDate}&endDate=${endDate}`, { headers }),
        month
          ? fetch(`/api/auth/birthdays?month=${month}`, { headers })
          : fetch('/api/auth/birthdays', { headers })
      ]);

      if (callsResp.ok) {
        const data = await callsResp.json();
        setCalls(Array.isArray(data) ? data.filter((c) => !REMOVED_CALL_TYPES.has(c.call_type)) : []);
      }
      if (eventsResp.ok) setCustomEvents(await eventsResp.json());
      if (bdResp.ok) setBirthdays(await bdResp.json());
    } catch (e) { console.error('Calendar load error:', e); }
    finally { setLoading(false); }
  }, [getDateRange]);

  useEffect(() => { loadData(); }, [loadData]);

  // Sync dashboard widget
  const dispatchUpdate = () => { window.dispatchEvent(new CustomEvent('callsScheduleUpdated')); };

  // --- Event maps ---
  const eventsForDay = useCallback((dateKey) => {
    const result = [];
    calls.forEach(c => {
      const dk = toDateKey(typeof c.call_date === 'string' ? c.call_date : c.call_date);
      if (dk === dateKey) result.push({ ...c, _type: 'call' });
    });
    customEvents.forEach(e => {
      const dk = toDateKey(typeof e.event_date === 'string' ? e.event_date : e.event_date);
      if (dk === dateKey) result.push({ ...e, _type: 'event' });
    });
    const md = dateKey.slice(5); // MM-DD
    birthdays.forEach(b => {
      if (b.birthDate && b.birthDate.slice(5) === md) {
        result.push({ _type: 'birthday', discordUsername: b.discordUsername, birthDate: b.birthDate });
      }
    });
    return result;
  }, [calls, customEvents, birthdays]);

  // --- Navigation ---
  const prevMonth = () => { setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)); };
  const nextMonth = () => { setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)); };
  const goToday = () => {
    const now = new Date(); now.setHours(0, 0, 0, 0);
    if (view === 'month') { setCurrentDate(new Date(now.getFullYear(), now.getMonth(), 1)); }
    else { setWeekStart(getMonday(now)); }
  };
  const prevWeek = () => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); };
  const nextWeek = () => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); };

  // --- Day click ---
  const handleDayClick = (dateKey) => {
    setSelectedDay(dateKey);
    setDayPanelOpen(true);
  };
  const closeDayPanel = () => { setDayPanelOpen(false); };

  // --- CRUD: Calls ---
  const openAddCall = (dateKey) => {
    setEditingCall(null);
    setCallForm({ call_type: '', call_date: dateKey || toDateKey(getMskToday()), call_time: '12:00', participants: [] });
    setParticipantSearch('');
    setEventModalTab('call');
    setEventModalOpen(true);
  };
  const openEditCall = (call) => {
    setEditingCall(call);
    let participants = call.participants || [];
    if (typeof participants === 'string') { try { participants = JSON.parse(participants); } catch { participants = []; } }
    if (!Array.isArray(participants)) participants = [];
    const normalized = participants.map(p => {
      if (typeof p === 'object' && p.discord_id) return p;
      if (typeof p === 'string') return { discord_id: p, nickname: p };
      return null;
    }).filter(Boolean);
    const dateStr = typeof call.call_date === 'string' ? call.call_date.slice(0, 10) : toDateKey(call.call_date);
    setCallForm({ call_type: call.call_type || '', call_date: dateStr, call_time: call.call_time || '', participants: normalized });
    setParticipantSearch('');
    setEventModalTab('call');
    setEventModalOpen(true);
  };
  const saveCall = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = editingCall ? `/api/calls-schedule/${editingCall.id}` : '/api/calls-schedule';
      const method = editingCall ? 'PUT' : 'POST';
      const resp = await fetch(url, { method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(callForm) });
      if (resp.ok) { setEventModalOpen(false); loadData(); dispatchUpdate(); showAlert('Успех', editingCall ? 'Созвон обновлён' : 'Созвон создан', 'success'); }
      else { const e = await resp.json(); throw new Error(e.error || 'Ошибка'); }
    } catch (e) { showAlert('Ошибка', e.message || 'Не удалось сохранить созвон', 'error'); }
  };

  // --- CRUD: Custom events ---
  const openAddEvent = (dateKey) => {
    setEditingEvent(null);
    setEventForm({ title: '', event_date: dateKey || toDateKey(getMskToday()), event_time: '', description: '', color: '#22c55e', participants: [] });
    setParticipantSearch('');
    setEventModalTab('event');
    setEventModalOpen(true);
  };
  const openEditEvent = (ev) => {
    setEditingEvent(ev);
    const dateStr = typeof ev.event_date === 'string' ? ev.event_date.slice(0, 10) : toDateKey(ev.event_date);
    let parts = ev.participants || [];
    if (typeof parts === 'string') { try { parts = JSON.parse(parts); } catch { parts = []; } }
    if (!Array.isArray(parts)) parts = [];
    const normalized = parts.map(p => {
      if (typeof p === 'object' && p.discord_id) return p;
      return null;
    }).filter(Boolean);
    setEventForm({ title: ev.title || '', event_date: dateStr, event_time: ev.event_time ? formatTime(ev.event_time) : '', description: ev.description || '', color: ev.color || '#22c55e', participants: normalized });
    setParticipantSearch('');
    setEventModalTab('event');
    setEventModalOpen(true);
  };
  const saveEvent = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = editingEvent ? `/api/calendar-events/${editingEvent.id}` : '/api/calendar-events';
      const method = editingEvent ? 'PUT' : 'POST';
      const resp = await fetch(url, { method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(eventForm) });
      if (resp.ok) { setEventModalOpen(false); loadData(); dispatchUpdate(); showAlert('Успех', editingEvent ? 'Событие обновлено' : 'Событие создано', 'success'); }
      else { const e = await resp.json(); throw new Error(e.error || 'Ошибка'); }
    } catch (e) { showAlert('Ошибка', e.message || 'Не удалось сохранить событие', 'error'); }
  };

  // --- Delete ---
  const requestDelete = (item) => { setItemToDelete(item); setDeleteModalOpen(true); };
  const confirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      const token = localStorage.getItem('token');
      const url = itemToDelete._type === 'call' ? `/api/calls-schedule/${itemToDelete.id}` : `/api/calendar-events/${itemToDelete.id}`;
      const resp = await fetch(url, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (resp.ok) { setDeleteModalOpen(false); setItemToDelete(null); loadData(); dispatchUpdate(); showAlert('Успех', 'Удалено', 'success'); }
      else throw new Error('Ошибка при удалении');
    } catch (e) { showAlert('Ошибка', e.message, 'error'); }
  };

  const toggleCallParticipant = (mod) => {
    setCallForm(prev => {
      const list = [...prev.participants];
      const idx = list.findIndex(p => p.discord_id === mod.discord_id);
      if (idx > -1) list.splice(idx, 1);
      else list.push({ discord_id: mod.discord_id, nickname: mod.nickname });
      return { ...prev, participants: list };
    });
  };
  const toggleEventParticipant = (mod) => {
    setEventForm(prev => {
      const list = [...prev.participants];
      const idx = list.findIndex(p => p.discord_id === mod.discord_id);
      if (idx > -1) list.splice(idx, 1);
      else list.push({ discord_id: mod.discord_id, nickname: mod.nickname });
      return { ...prev, participants: list };
    });
  };

  // --- Build month grid ---
  const buildMonthGrid = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDay = firstDay.getDay();
    const offset = startDay === 0 ? -6 : 1 - startDay;
    const gridStart = new Date(year, month, 1 + offset);
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      cells.push(d);
    }
    // Trim trailing week if entirely next month
    while (cells.length > 35 && cells[cells.length - 7].getMonth() !== month) {
      cells.splice(-7);
    }
    return cells;
  };

  // --- Build week grid ---
  const buildWeekGrid = () => {
    const cells = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      cells.push(d);
    }
    return cells;
  };

  const todayKey = toDateKey(getMskToday());
  const curMonth = currentDate.getMonth();

  // --- Render event pill ---
  const renderPill = (item, idx, compact = false) => {
    if (item._type === 'birthday') {
      return (
        <div key={`bd-${idx}`} className="cal-pill cal-pill-birthday" title={`🎂 ${item.discordUsername}`}>
          <span className="cal-pill-icon">🎂</span>
          {!compact && <span className="cal-pill-text">{item.discordUsername}</span>}
        </div>
      );
    }
    if (item._type === 'call') {
      const label = getCallTypeLabel(item.call_type);
      const short = label.length > 28 ? label.slice(0, 26) + '…' : label;
      return (
        <div key={`call-${item.id}-${idx}`} className="cal-pill cal-pill-call" title={`${label} — ${formatTime(item.call_time)}`}>
          <span className="cal-pill-dot" style={{ background: '#ff8000' }} />
          {!compact && <span className="cal-pill-text">{formatTime(item.call_time)} {short}</span>}
        </div>
      );
    }
    return (
      <div key={`ev-${item.id}-${idx}`} className="cal-pill cal-pill-event" title={`${item.title}${item.event_time ? ' — ' + formatTime(item.event_time) : ''}`}>
        <span className="cal-pill-dot" style={{ background: item.color || '#22c55e' }} />
        {!compact && <span className="cal-pill-text">{item.event_time ? formatTime(item.event_time) + ' ' : ''}{item.title}</span>}
      </div>
    );
  };

  // --- Render day detail panel ---
  const renderDayDetail = () => {
    if (!selectedDay) return null;
    const items = eventsForDay(selectedDay);
    const date = parseLocalDate(selectedDay);
    const dayLabel = `${date.getDate()} ${MONTH_NAMES_GEN[date.getMonth()]} ${date.getFullYear()}`;
    const weekDay = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'][date.getDay()];

    return (
      <div className={`cal-day-panel ${dayPanelOpen ? 'open' : ''}`} ref={dayPanelRef}>
        <div className="cal-day-panel-header">
          <div className="cal-day-panel-title">
            <span className="cal-day-panel-date">{dayLabel}</span>
            <span className="cal-day-panel-weekday">{weekDay}</span>
          </div>
          <button className="cal-day-panel-close" onClick={closeDayPanel} aria-label="Закрыть">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div className="cal-day-panel-body">
          {items.length === 0 ? (
            <div className="cal-day-panel-empty">Нет событий</div>
          ) : items.map((item, i) => (
            <div key={i} className={`cal-day-detail-item cal-day-detail-${item._type}`}>
              {item._type === 'birthday' ? (
                <div className="cal-day-detail-content">
                  <span className="cal-day-detail-icon">🎂</span>
                  <span className="cal-day-detail-text">День рождения — {item.discordUsername}</span>
                </div>
              ) : item._type === 'call' ? (
                <>
                  <div className="cal-day-detail-content">
                    <span className="cal-day-detail-dot" style={{ background: '#ff8000' }} />
                    <div className="cal-day-detail-info">
                      <span className="cal-day-detail-text">{getCallTypeLabel(item.call_type)}</span>
                      {item.call_time && <span className="cal-day-detail-time">{formatTime(item.call_time)}</span>}
                      {(() => {
                        let parts = item.participants || [];
                        if (typeof parts === 'string') { try { parts = JSON.parse(parts); } catch { parts = []; } }
                        if (!Array.isArray(parts) || parts.length === 0) return null;
                        return (
                          <div className="cal-day-detail-participants">
                            {parts.map((p, j) => (
                              <span key={j} className="cal-day-detail-badge">
                                {typeof p === 'object' && p.nickname ? p.nickname : String(p)}
                              </span>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  {canEdit() && (
                    <div className="cal-day-detail-actions">
                      <button className="cal-day-detail-edit" onClick={() => openEditCall(item)} title="Редактировать">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      </button>
                      <button className="cal-day-detail-delete" onClick={() => requestDelete({ ...item, _type: 'call' })} title="Удалить">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="cal-day-detail-content">
                    <span className="cal-day-detail-dot" style={{ background: item.color || '#22c55e' }} />
                    <div className="cal-day-detail-info">
                      <span className="cal-day-detail-text">{item.title}</span>
                      {item.event_time && <span className="cal-day-detail-time">{formatTime(item.event_time)}</span>}
                      {item.description && <span className="cal-day-detail-desc">{item.description}</span>}
                      {(() => {
                        let parts = item.participants || [];
                        if (typeof parts === 'string') { try { parts = JSON.parse(parts); } catch { parts = []; } }
                        if (!Array.isArray(parts) || parts.length === 0) return null;
                        return (
                          <div className="cal-day-detail-participants">
                            {parts.map((p, j) => (
                              <span key={j} className="cal-day-detail-badge" style={{ borderColor: `${item.color || '#22c55e'}40`, color: item.color || '#22c55e', background: `${item.color || '#22c55e'}18` }}>
                                {typeof p === 'object' && p.nickname ? p.nickname : String(p)}
                              </span>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  {canEdit() && (
                    <div className="cal-day-detail-actions">
                      <button className="cal-day-detail-edit" onClick={() => openEditEvent(item)} title="Редактировать">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      </button>
                      <button className="cal-day-detail-delete" onClick={() => requestDelete({ ...item, _type: 'event' })} title="Удалить">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
        {canEdit() && (
          <div className="cal-day-panel-footer">
            <button className="cal-day-panel-add" onClick={() => openAddCall(selectedDay)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Созвон
            </button>
            <button className="cal-day-panel-add cal-day-panel-add-event" onClick={() => openAddEvent(selectedDay)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Событие
            </button>
          </div>
        )}
      </div>
    );
  };

  // --- Render event modal ---
  const renderEventModal = () => {
    const isCall = eventModalTab === 'call';
    const modalTitle = isCall
      ? (editingCall ? 'Редактировать созвон' : 'Новый созвон')
      : (editingEvent ? 'Редактировать событие' : 'Новое событие');

    return (
      <SettingsModal
        isOpen={eventModalOpen}
        onClose={() => setEventModalOpen(false)}
        title={modalTitle}
        size="lg"
      >
        <div className="cal-event-modal">
          <div className="cal-event-tabs">
            <button className={`cal-event-tab ${isCall ? 'active' : ''}`} onClick={() => setEventModalTab('call')}>Созвон</button>
            <button className={`cal-event-tab ${!isCall ? 'active' : ''}`} onClick={() => setEventModalTab('event')}>Событие</button>
          </div>

          {isCall ? (
            <div className="cal-event-form">
              <div className="form-group">
                <label>Тип созвона</label>
                <select value={callForm.call_type} onChange={e => setCallForm({ ...callForm, call_type: e.target.value })} disabled={!!editingCall}>
                  <option value="">Выберите тип</option>
                  {CALL_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Дата</label>
                <div className="date-picker-wrapper">
                  <DatePicker
                    selectedDate={callForm.call_date ? (() => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(callForm.call_date); return m ? new Date(+m[1], +m[2]-1, +m[3]) : new Date(); })() : new Date()}
                    onDateChange={d => { if (d instanceof Date && !isNaN(d.getTime())) setCallForm(p => ({ ...p, call_date: toDateKey(d) })); }}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Время</label>
                <TimePicker
                  value={callForm.call_time}
                  onChange={(call_time) => setCallForm({ ...callForm, call_time })}
                />
              </div>
              <div className="form-group">
                <div className="participants-header">
                  <label>Участники ({callForm.participants.length})</label>
                  <div className="participants-actions">
                    <button type="button" className="participants-select-all"
                      onClick={() => setCallForm(p => ({
                        ...p,
                        participants: p.participants.length === moderatorsList.length
                          ? []
                          : moderatorsList.map(m => ({ discord_id: m.discord_id, nickname: m.nickname }))
                      }))}>
                      {callForm.participants.length === moderatorsList.length ? 'Снять всех' : 'Выбрать всех'}
                    </button>
                  </div>
                </div>
                <input
                  type="text"
                  className="participants-search"
                  placeholder="Поиск по никнейму..."
                  value={participantSearch}
                  onChange={e => setParticipantSearch(e.target.value)}
                />
                <div className="participants-checkboxes">
                  {moderatorsList
                    .filter(m => !participantSearch || m.nickname.toLowerCase().includes(participantSearch.toLowerCase()))
                    .map(mod => (
                    <label key={mod.discord_id} className="participant-checkbox">
                      <input
                        type="checkbox"
                        checked={callForm.participants.some(p => p.discord_id === mod.discord_id)}
                        onChange={() => toggleCallParticipant(mod)}
                      />
                      <span>{mod.nickname}</span>
                    </label>
                  ))}
                  {moderatorsList.length === 0 && (
                    <div style={{ color: '#737373', fontSize: 12, padding: '8px 0' }}>Нет доступных модераторов</div>
                  )}
                </div>
              </div>
              <div className="form-actions">
                <button className="btn-cancel" onClick={() => setEventModalOpen(false)}>Отмена</button>
                <button className="btn-save" onClick={saveCall}>{editingCall ? 'Сохранить' : 'Создать'}</button>
              </div>
            </div>
          ) : (
            <div className="cal-event-form">
              <div className="form-group">
                <label>Название</label>
                <input type="text" value={eventForm.title} onChange={e => setEventForm({ ...eventForm, title: e.target.value })} placeholder="Введите название" maxLength={200} />
              </div>
              <div className="form-group">
                <label>Дата</label>
                <div className="date-picker-wrapper">
                  <DatePicker
                    selectedDate={eventForm.event_date ? (() => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(eventForm.event_date); return m ? new Date(+m[1], +m[2]-1, +m[3]) : new Date(); })() : new Date()}
                    onDateChange={d => { if (d instanceof Date && !isNaN(d.getTime())) setEventForm(p => ({ ...p, event_date: toDateKey(d) })); }}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Время (необязательно)</label>
                <TimePicker
                  value={eventForm.event_time}
                  onChange={(event_time) => setEventForm({ ...eventForm, event_time })}
                  allowEmpty
                  placeholder="Без времени"
                />
              </div>
              <div className="form-group">
                <label>Описание (необязательно)</label>
                <textarea value={eventForm.description} onChange={e => setEventForm({ ...eventForm, description: e.target.value })} placeholder="Добавьте описание" rows={3} maxLength={1000} />
              </div>
              <div className="form-group">
                <label>Цвет метки</label>
                <div className="cal-color-picker">
                  {EVENT_COLORS.map(c => (
                    <button key={c} type="button" className={`cal-color-swatch ${eventForm.color === c ? 'active' : ''}`} style={{ background: c }} onClick={() => setEventForm({ ...eventForm, color: c })} aria-label={c} />
                  ))}
                </div>
              </div>
              <div className="form-group">
                <div className="participants-header">
                  <label>Участники ({eventForm.participants.length})</label>
                  <div className="participants-actions">
                    <button type="button" className="participants-select-all"
                      onClick={() => setEventForm(p => ({
                        ...p,
                        participants: p.participants.length === moderatorsList.length
                          ? []
                          : moderatorsList.map(m => ({ discord_id: m.discord_id, nickname: m.nickname }))
                      }))}>
                      {eventForm.participants.length === moderatorsList.length ? 'Снять всех' : 'Выбрать всех'}
                    </button>
                  </div>
                </div>
                <input
                  type="text"
                  className="participants-search"
                  placeholder="Поиск по никнейму..."
                  value={participantSearch}
                  onChange={e => setParticipantSearch(e.target.value)}
                />
                <div className="participants-checkboxes">
                  {moderatorsList
                    .filter(m => !participantSearch || m.nickname.toLowerCase().includes(participantSearch.toLowerCase()))
                    .map(mod => (
                    <label key={mod.discord_id} className="participant-checkbox">
                      <input
                        type="checkbox"
                        checked={eventForm.participants.some(p => p.discord_id === mod.discord_id)}
                        onChange={() => toggleEventParticipant(mod)}
                      />
                      <span>{mod.nickname}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="form-actions">
                <button className="btn-cancel" onClick={() => setEventModalOpen(false)}>Отмена</button>
                <button className="btn-save" onClick={saveEvent}>{editingEvent ? 'Сохранить' : 'Создать'}</button>
              </div>
            </div>
          )}
        </div>
      </SettingsModal>
    );
  };

  // --- Week header label ---
  const weekLabel = () => {
    const end = new Date(weekStart);
    end.setDate(end.getDate() + 6);
    const sameMonth = weekStart.getMonth() === end.getMonth();
    if (sameMonth) return `${weekStart.getDate()}–${end.getDate()} ${MONTH_NAMES_GEN[weekStart.getMonth()]} ${weekStart.getFullYear()}`;
    return `${weekStart.getDate()} ${MONTH_NAMES_GEN[weekStart.getMonth()]} – ${end.getDate()} ${MONTH_NAMES_GEN[end.getMonth()]} ${end.getFullYear()}`;
  };

  // Close day panel on outside click
  useEffect(() => {
    if (!dayPanelOpen) return;
    const handler = (e) => {
      if (dayPanelRef.current && !dayPanelRef.current.contains(e.target) && !e.target.closest('.cal-cell')) {
        closeDayPanel();
      }
    };
    setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => document.removeEventListener('mousedown', handler);
  }, [dayPanelOpen]);

  if (loading && calls.length === 0 && customEvents.length === 0) {
    return (
      <div className="cal-loading">
        <div className="loader" />
        <p>Загрузка календаря...</p>
      </div>
    );
  }

  return (
    <div className="cal-container">
      {/* Header */}
      <div className="cal-header">
        <div className="cal-header-left">
          {view === 'month' ? (
            <>
              <button className="cal-nav-btn" onClick={prevMonth} aria-label="Предыдущий месяц">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
              <h2 className="cal-title">{MONTH_NAMES[currentDate.getMonth()]} {currentDate.getFullYear()}</h2>
              <button className="cal-nav-btn" onClick={nextMonth} aria-label="Следующий месяц">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            </>
          ) : (
            <>
              <button className="cal-nav-btn" onClick={prevWeek} aria-label="Предыдущая неделя">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
              <h2 className="cal-title">{weekLabel()}</h2>
              <button className="cal-nav-btn" onClick={nextWeek} aria-label="Следующая неделя">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            </>
          )}
          <button className="cal-today-btn" onClick={goToday}>Сегодня</button>
        </div>
        <div className="cal-header-center">
          <div className="cal-view-toggle">
            <button className={`cal-view-btn ${view === 'month' ? 'active' : ''}`} onClick={() => setView('month')}>Месяц</button>
            <button className={`cal-view-btn ${view === 'week' ? 'active' : ''}`} onClick={() => setView('week')}>Неделя</button>
          </div>
        </div>
        <div className="cal-header-right">
          {canEdit() && (
            <button className="cal-add-btn" onClick={() => { openAddCall(null); }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              <span>Создать</span>
            </button>
          )}
        </div>
      </div>

      {/* Calendar body */}
      <div className={`cal-body ${dayPanelOpen ? 'with-panel' : ''}`}>
        <div className="cal-grid-wrap">
          {/* Weekday headers */}
          <div className={`cal-weekdays ${view === 'week' ? 'cal-weekdays-week' : ''}`}>
            {WEEK_DAYS.map(d => <div key={d} className="cal-weekday">{d}</div>)}
          </div>

          {view === 'month' ? (
            <div className="cal-month-grid">
              {buildMonthGrid().map((d, i) => {
                const dk = toDateKey(d);
                const items = eventsForDay(dk);
                const isOtherMonth = d.getMonth() !== curMonth;
                const isToday = dk === todayKey;
                const isSelected = dk === selectedDay && dayPanelOpen;
                const MAX_PILLS = 3;
                const shown = items.slice(0, MAX_PILLS);
                const overflow = items.length - MAX_PILLS;
                return (
                  <div key={i}
                    className={`cal-cell ${isOtherMonth ? 'other-month' : ''} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${items.length > 0 ? 'has-events' : ''}`}
                    onClick={() => handleDayClick(dk)}
                  >
                    <span className="cal-cell-day">{d.getDate()}</span>
                    <div className="cal-cell-pills">
                      {shown.map((item, j) => renderPill(item, j, false))}
                      {overflow > 0 && <div className="cal-pill-overflow">+{overflow}</div>}
                    </div>
                    {/* Mobile dots */}
                    {items.length > 0 && (
                      <div className="cal-cell-dots">
                        {items.slice(0, 4).map((item, j) => (
                          <span key={j} className="cal-cell-dot" style={{
                            background: item._type === 'birthday' ? '#f472b6' : item._type === 'call' ? '#ff8000' : (item.color || '#22c55e')
                          }} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="cal-week-grid">
              {buildWeekGrid().map((d, i) => {
                const dk = toDateKey(d);
                const items = eventsForDay(dk);
                const isToday = dk === todayKey;
                const isSelected = dk === selectedDay && dayPanelOpen;
                return (
                  <div key={i}
                    className={`cal-cell cal-cell-week ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${items.length > 0 ? 'has-events' : ''}`}
                    onClick={() => handleDayClick(dk)}
                  >
                    <div className="cal-cell-week-header">
                      <span className="cal-cell-day">{d.getDate()}</span>
                      <span className="cal-cell-month-label">{MONTH_NAMES_GEN[d.getMonth()]}</span>
                      <span className="cal-cell-weekday-label">{WEEK_DAYS_FULL[d.getDay()]}</span>
                    </div>
                    <div className="cal-cell-pills">
                      {items.map((item, j) => renderPill(item, j, false))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Day detail panel — desktop only (inside cal-body) */}
        {!isMobile && renderDayDetail()}
      </div>

      {/* Day detail panel + overlay — mobile (portaled to body) */}
      {isMobile && createPortal(
        <>
          {dayPanelOpen && <div className="cal-day-panel-overlay" onClick={closeDayPanel} />}
          {renderDayDetail()}
        </>,
        document.body
      )}

      {/* Event modal */}
      {renderEventModal()}

      {/* Confirm delete */}
      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Удалить"
        message={itemToDelete?._type === 'call' ? 'Удалить этот созвон?' : 'Удалить это событие?'}
      />

      {/* Alert */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
      />
    </div>
  );
};

export default Calendar;
